import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

// Shared blob URL cache so thumbnails and the viewer load each photo once.
const cache = new Map();
function loadPhoto(path) {
  if (!cache.has(path)) {
    const p = api.get(`/files/${path}`, { responseType: "blob" })
      .then((r) => URL.createObjectURL(r.data))
      .catch((e) => { cache.delete(path); throw e; });
    cache.set(path, p);
  }
  return cache.get(path);
}

function usePhoto(path) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    setUrl(null);
    if (path) loadPhoto(path).then((u) => alive && setUrl(u)).catch(() => {});
    return () => { alive = false; };
  }, [path]);
  return url;
}

function PhotoViewer({ paths, start, onClose }) {
  const [i, setI] = useState(start);
  const n = paths.length;
  const url = usePhoto(paths[i]);
  const touchX = useRef(null);
  const prev = useCallback(() => setI((x) => (x - 1 + n) % n), [n]);
  const next = useCallback(() => setI((x) => (x + 1) % n), [n]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey, true); document.body.style.overflow = prevOverflow; };
  }, [prev, next, onClose]);

  // Warm the neighbors so swiping feels instant.
  useEffect(() => {
    if (n > 1) { loadPhoto(paths[(i + 1) % n]).catch(() => {}); loadPhoto(paths[(i - 1 + n) % n]).catch(() => {}); }
  }, [i, n, paths]);

  const stop = (e) => e.stopPropagation();
  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/90 flex flex-col select-none"
      onClick={onClose}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null || n < 2) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 50) (dx > 0 ? prev() : next());
      }}
      data-testid="photo-viewer"
    >
      <div className="flex items-center justify-between px-3 py-2 text-cream" onClick={stop}>
        <span className="font-heading font-bold text-sm" data-testid="photo-viewer-count">{n > 1 ? `${i + 1} / ${n}` : ""}</span>
        <div className="flex items-center gap-1">
          {url && (
            <a href={url} target="_blank" rel="noreferrer" aria-label="Open full size"
              className="w-11 h-11 inline-flex items-center justify-center rounded-full hover:bg-white/10"><ExternalLink className="w-5 h-5" /></a>
          )}
          <button type="button" onClick={onClose} aria-label="Close photos" data-testid="photo-viewer-close"
            className="w-11 h-11 inline-flex items-center justify-center rounded-full hover:bg-white/10"><X className="w-7 h-7" /></button>
        </div>
      </div>
      <div className="relative flex-1 flex items-center justify-center px-2 pb-4 min-h-0">
        {url
          ? <img src={url} alt={`Photo ${i + 1} of ${n}`} onClick={stop} className="max-w-full max-h-full object-contain rounded-lg" />
          : <div className="w-10 h-10 rounded-full border-4 border-cream/30 border-t-cream animate-spin" />}
        {n > 1 && (
          <>
            <button type="button" onClick={(e) => { stop(e); prev(); }} aria-label="Previous photo" data-testid="photo-viewer-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 inline-flex items-center justify-center rounded-full bg-black/50 text-cream hover:bg-black/70"><ChevronLeft className="w-7 h-7" /></button>
            <button type="button" onClick={(e) => { stop(e); next(); }} aria-label="Next photo" data-testid="photo-viewer-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 inline-flex items-center justify-center rounded-full bg-black/50 text-cream hover:bg-black/70"><ChevronRight className="w-7 h-7" /></button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// Thumbnail that opens a swipeable viewer over every photo in `group`.
export function PhotoThumb({ path, size = "w-14 h-14", group }) {
  const url = usePhoto(path);
  const [open, setOpen] = useState(false);
  const paths = group && group.length ? group : [path];
  const start = Math.max(0, paths.indexOf(path));
  if (!url) return <div className={`${size} rounded-lg bg-charcoal/10 animate-pulse`} />;
  return (
    <>
      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }} aria-label="View photo" data-testid="photo-thumb" className="block">
        <img src={url} alt="job" className={`${size} rounded-lg object-cover border-2 border-charcoal`} />
      </button>
      {open && <PhotoViewer paths={paths} start={start} onClose={() => setOpen(false)} />}
    </>
  );
}
